import React from 'react';
import Navbar from '../components/Navbar';
import Footer from '../components/Footer';
import { Shield, Target, TrendingUp, Users, Award, ArrowRight } from 'lucide-react';
import { Link } from 'react-router-dom';

const TeamPage = () => {
  return (
    <div className="min-h-screen bg-black">
      <Navbar />

      {/* Hero */}
      <section className="pt-32 pb-20 relative overflow-hidden">
        <div className="absolute inset-0">
          <div className="absolute top-1/4 right-1/4 w-96 h-96 bg-white/5 rounded-full blur-3xl" />
        </div>
        <div className="max-w-7xl mx-auto px-6 lg:px-8 relative z-10">
          <div className="max-w-3xl">
            <span className="text-white/40 text-sm uppercase tracking-widest mb-4 block">ABOUT US</span>
            <h1 className="text-5xl lg:text-6xl font-bold text-white mb-6" data-testid="about-heading">
              Built on Trust,
              <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-white to-white/60">
                Driven by Purpose
              </span>
            </h1>
            <p className="text-lg text-white/60 leading-relaxed">
              Frontier Investments is an AMFI certified Mutual Fund Distributor (ARN-92591) dedicated to helping
              individuals and families make informed, confident investment decisions. With over two decades
              of experience in the mutual fund industry, we combine deep market expertise with a client-first approach.
            </p>
          </div>
        </div>
      </section>

      {/* Our Story */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f5f5f5 100%)' }}>
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-16 items-center">
            <div>
              <span className="text-emerald-600 text-sm font-medium uppercase tracking-widest mb-3 block">Our Story</span>
              <h2 className="text-3xl lg:text-4xl font-bold text-gray-900 mb-6">A Legacy of Thoughtful Investing</h2>
              <div className="space-y-4 text-gray-600 leading-relaxed">
                <p>
                  Founded with a simple belief — that every investor deserves honest, transparent and
                  professional guidance — Frontier Investments has grown into a trusted name in the Indian
                  mutual fund distribution landscape. We began our journey by serving a handful of families and
                  today, we are proud to guide over 1000 clients across India and abroad.
                </p>
                <p>
                  Our approach is rooted in fundamentals. We don't chase trends or make promises we can't keep.
                  Instead, we focus on building well-diversified, risk-adjusted portfolios tailored to each
                  client's unique goals, timeline and risk appetite. Whether you are planning for retirement,
                  your child's education, or simply looking to grow your wealth — we bring clarity, structure
                  and purpose to every decision.
                </p>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-4">
              {[
                { number: '20+', label: 'Years of Experience', icon: Award },
                { number: '1000+', label: 'Clients Served', icon: Users },
                { number: 'Zero', label: 'Hidden Charges', icon: Shield },
                { number: 'Pan-India', label: 'Client Base', icon: TrendingUp },
              ].map((stat, i) => (
                <div key={i} className="bg-white rounded-2xl p-6 border border-gray-200 shadow-sm text-center" data-testid={`about-stat-${i}`}>
                  <stat.icon className="w-6 h-6 text-emerald-600 mx-auto mb-3" />
                  <p className="text-2xl font-bold text-gray-900">{stat.number}</p>
                  <p className="text-gray-500 text-sm mt-1">{stat.label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* What We Believe */}
      <section className="py-20 bg-black">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-white/40 text-sm uppercase tracking-widest mb-3 block">OUR PHILOSOPHY</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-white">What We Believe In</h2>
          </div>
          <div className="grid md:grid-cols-3 gap-8">
            {[
              {
                icon: Shield,
                title: 'Transparency First',
                desc: 'No hidden fees, no opaque products. We believe in complete transparency in every recommendation we make and every fee we charge.'
              },
              {
                icon: Target,
                title: 'Process-Driven Investing',
                desc: 'We follow a disciplined, research-backed investment framework. Every portfolio is constructed on robust fundamentals, not market noise.'
              },
              {
                icon: Users,
                title: 'Client-Centric Approach',
                desc: 'Your goals are our priority. We take the time to understand your aspirations, risk profile and life stage before crafting a personalized strategy.'
              },
            ].map((item, i) => (
              <div key={i} className="bg-white/5 border border-white/10 rounded-2xl p-8 hover:bg-white/10 transition-colors duration-300" data-testid={`about-belief-${i}`}>
                <div className="p-3 bg-emerald-500/20 rounded-xl w-fit mb-5">
                  <item.icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-white font-bold text-lg mb-3">{item.title}</h3>
                <p className="text-white/50 text-sm leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How We Work */}
      <section className="py-20" style={{ background: 'linear-gradient(135deg, #f8f9fa 0%, #ffffff 50%, #f5f5f5 100%)' }}>
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-16">
            <span className="text-emerald-600 text-sm font-medium uppercase tracking-widest mb-3 block">OUR PROCESS</span>
            <h2 className="text-3xl lg:text-4xl font-bold text-gray-900">How We Work With You</h2>
          </div>
          <div className="space-y-6">
            {[
              { step: '01', title: 'Understand', desc: 'We begin by listening — understanding your financial goals, current portfolio, risk tolerance and investment horizon.' },
              { step: '02', title: 'Plan', desc: 'Our team designs a comprehensive investment plan tailored to your specific needs, incorporating asset allocation, diversification and tax efficiency.' },
              { step: '03', title: 'Execute', desc: 'We implement the plan with precision, selecting the right instruments and timing to build your portfolio brick by brick.' },
              { step: '04', title: 'Monitor & Review', desc: 'Markets evolve, and so do your goals. We proactively review and rebalance your portfolio to keep it aligned with your objectives.' },
            ].map((item, i) => (
              <div key={i} className="flex gap-6 items-start bg-white rounded-2xl p-6 border border-gray-200 shadow-sm" data-testid={`about-step-${i}`}>
                <span className="text-3xl font-bold text-emerald-500/30 flex-shrink-0">{item.step}</span>
                <div>
                  <h3 className="text-gray-900 font-bold text-lg mb-1">{item.title}</h3>
                  <p className="text-gray-500 text-sm leading-relaxed">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 bg-black">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-bold text-white mb-4">
            Ready to start your investment journey?
          </h2>
          <p className="text-white/60 mb-10 max-w-2xl mx-auto">
            Whether you're a first-time investor or looking for a fresh perspective on your existing portfolio,
            we're here to help.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/portfolio-upload"
              data-testid="about-cta-portfolio"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white text-black font-semibold rounded-full hover:shadow-[0_0_40px_rgba(255,255,255,0.3)] transition-all duration-500 group"
            >
              Review My Portfolio
              <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
            </Link>
            <Link
              to="/contact"
              className="inline-flex items-center justify-center gap-3 px-8 py-4 bg-white/10 text-white font-semibold rounded-full border border-white/20 hover:bg-white/20 transition-all duration-300"
            >
              Contact Us
            </Link>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default TeamPage;
